import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  FiberManualRecord as RecordIcon,
  PlayArrow,
  Pause,
  Stop,
  Download,
  Search,
  FilterList,
  Refresh,
  CloudUpload,
  CloudDone,
  CloudOff,
  VolumeUp,
  AccessTime,
  Add as AddIcon,
  DeleteOutline,
  Link as LinkIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function Logger() {
  const { user, isSuperAdmin, isAdmin } = useAuth();
  const [stations, setStations] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  // Filters
  const [filterStation, setFilterStation] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  // Audio player state
  const [playingId, setPlayingId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // Config dialog
  const [configDialogOpen, setConfigDialogOpen] = useState(false);

  // Custom stream URL form
  const [customStation, setCustomStation] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [customUrl, setCustomUrl] = useState('');

  useEffect(() => {
    fetchStations();
    fetchConfigs();
    fetchRecordings();
  }, []);

  // Single station mode: default filter to the only station
  useEffect(() => {
    if (stations.length === 1) {
      setFilterStation(stations[0].id);
      setCustomStation(stations[0].id);
    }
  }, [stations]);

  useEffect(() => {
    fetchRecordings();
  }, [page, filterStation, filterDate]);

  const fetchStations = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stations`, { withCredentials: true });
      setStations(res.data || []);
    } catch (err) {
      console.error('Error fetching stations:', err);
    }
  };

  const fetchConfigs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/logger/configs`, { withCredentials: true });
      setConfigs(res.data || []);
    } catch (err) {
      console.error('Error fetching logger configs:', err);
      setConfigs([]);
    }
  };

  const fetchRecordings = async (pageOverride) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pageOverride ?? page, limit: 30 });
      if (filterStation) params.append('station_id', filterStation);
      if (filterDate) params.append('date', filterDate);
      if (filterSearch) params.append('search', filterSearch);

      const res = await axios.get(`${API_URL}/api/logger/recordings?${params}`, { withCredentials: true });
      setRecordings(res.data.recordings || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 0);
    } catch (err) {
      console.error('Error fetching recordings:', err);
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchRecordings(1);
  };

  const handleToggleConfig = async (config) => {
    setConfigLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/logger/configs/${config.id}`,
        { enabled: !config.enabled },
        { withCredentials: true }
      );
      fetchConfigs();
    } catch (err) {
      console.error('Error toggling config:', err);
      alert(err.response?.data?.message || 'Failed to toggle recording');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleAddConfig = async (stationId, mountpoint, mountLabel) => {
    setConfigLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/logger/configs`,
        { station_id: stationId, mountpoint, mount_label: mountLabel, enabled: true },
        { withCredentials: true }
      );
      fetchConfigs();
    } catch (err) {
      console.error('Error adding config:', err);
      alert(err.response?.data?.message || 'Failed to add logger config');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleAddCustomConfig = async () => {
    const url = customUrl.trim();
    if (!customStation || !url) {
      alert('Select a station and enter a stream URL');
      return;
    }
    if (!/^https?:\/\/.+/i.test(url)) {
      alert('Stream URL must start with http:// or https://');
      return;
    }

    // Derive a mountpoint identifier from the URL path (used for the unique
    // key and the Dropbox folder name). Prefixed with "url-" so it can never
    // collide with a station's real icecast mountpoint config.
    let mountpoint;
    try {
      const pathname = new URL(url).pathname.replace(/^\//, '');
      mountpoint = pathname ? `/url-${pathname}` : `/url-${Date.now()}`;
    } catch {
      mountpoint = `/url-${Date.now()}`;
    }

    setConfigLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/logger/configs`,
        {
          station_id: customStation,
          mountpoint,
          mount_label: customLabel.trim() || url,
          stream_url: url,
          enabled: true,
        },
        { withCredentials: true }
      );
      setCustomLabel('');
      setCustomUrl('');
      fetchConfigs();
    } catch (err) {
      console.error('Error adding custom config:', err);
      alert(err.response?.data?.message || 'Failed to add stream URL');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleDeleteConfig = async (config) => {
    if (!window.confirm('Remove this recording configuration? Existing recordings are kept.')) return;
    setConfigLoading(true);
    try {
      await axios.delete(`${API_URL}/api/logger/configs/${config.id}`, { withCredentials: true });
      fetchConfigs();
    } catch (err) {
      console.error('Error deleting config:', err);
      alert(err.response?.data?.message || 'Failed to remove config');
    } finally {
      setConfigLoading(false);
    }
  };

  // Audio playback
  const handlePlay = async (recordingId) => {
    if (playingId === recordingId && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }

    if (playingId === recordingId && !isPlaying) {
      audioRef.current?.play();
      setIsPlaying(true);
      return;
    }

    // Load new audio
    try {
      const res = await axios.get(`${API_URL}/api/logger/recordings/${recordingId}/link`, { withCredentials: true });
      const url = res.data.source === 'local'
        ? `${API_URL}${res.data.url}`
        : res.data.url;

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      setPlayingId(recordingId);
      setAudioUrl(url);
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(true);

      // Play after setting the URL (useEffect on audioUrl will handle)
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = url;
          audioRef.current.play().catch(() => setIsPlaying(false));
        }
      }, 100);
    } catch (err) {
      console.error('Error getting recording link:', err);
      alert(err.response?.data?.message || 'Failed to load recording');
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlayingId(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  const handleDownload = async (recordingId) => {
    try {
      const res = await axios.get(`${API_URL}/api/logger/recordings/${recordingId}/link`, { withCredentials: true });
      const url = res.data.source === 'local'
        ? `${API_URL}/api/logger/recordings/${recordingId}/download`
        : res.data.url;

      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading:', err);
      alert('Failed to download recording');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const formatTime = (time) => {
    if (!time) return '';
    const d = new Date(time);
    return d.toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '--';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDropboxStatusColor = (status) => {
    switch (status) {
      case 'uploaded': return 'success';
      case 'error': return 'error';
      case 'pending': return 'warning';
      case 'no_client': return 'warning';
      case 'deleted': return 'default';
      default: return 'default';
    }
  };

  const getDropboxStatusIcon = (status) => {
    switch (status) {
      case 'uploaded': return <CloudDone sx={{ fontSize: 16 }} />;
      case 'error': return <CloudOff sx={{ fontSize: 16 }} />;
      default: return <CloudUpload sx={{ fontSize: 16 }} />;
    }
  };

  // Configs with an explicit stream URL (custom sources added by URL)
  const customConfigs = configs.filter(c => c.stream_url);

  // Build list of available mountpoints for the config dialog
  const availableMountpoints = stations.flatMap(station => {
    const mounts = [];
    if (station.icecast_enabled && station.icecast_mountpoint) {
      const mount = station.icecast_mountpoint.startsWith('/')
        ? station.icecast_mountpoint
        : `/${station.icecast_mountpoint}`;
      const existing = configs.find(c => c.station_id === station.id && c.mountpoint === mount && !c.stream_url);
      mounts.push({
        station_id: station.id,
        station_name: station.name,
        mountpoint: mount,
        label: `${station.name} (${mount})`,
        configured: !!existing,
        config: existing,
      });
    }
    return mounts;
  });

  return (
    <Box>
      <PageHeader
        overline="Compliance"
        title="Stream Logger"
        subtitle="Record and review your station output"
        icon={<RecordIcon />}
        actions={
          isSuperAdmin() && (
            <Button
              variant="contained"
              startIcon={<RecordIcon />}
              onClick={() => setConfigDialogOpen(true)}
            >
              Manage Recording
            </Button>
          )
        }
      />

      {/* Active Recordings Status */}
      {configs.filter(c => c.enabled).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <RecordIcon sx={{ color: 'secondary.main', fontSize: 18, animation: 'pulse 1.5s ease-in-out infinite' }} />
              <Typography variant="subtitle2" sx={{ color: 'secondary.main', fontWeight: 600 }}>
                Active Recordings
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {configs.filter(c => c.enabled).map(c => {
                const station = stations.find(s => s.id === c.station_id);
                return (
                  <Chip
                    key={c.id}
                    label={c.mount_label || `${station?.name || 'Station'} ${c.mountpoint}`}
                    size="small"
                    icon={<RecordIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{ bgcolor: 'rgba(239, 68, 68, 0.15)', color: 'secondary.light', borderColor: 'secondary.dark', border: '1px solid' }}
                  />
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {stations.length > 1 && (
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Station</InputLabel>
                  <Select
                    value={filterStation}
                    label="Station"
                    onChange={(e) => { setFilterStation(e.target.value); setPage(1); }}
                  >
                    <MenuItem value="">All Stations</MenuItem>
                    {stations.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={filterDate}
                onChange={(e) => { setFilterDate(e.target.value); setPage(1); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Search show name"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="e.g., Morning Show"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  fullWidth
                >
                  Search
                </Button>
                <IconButton
                  onClick={() => {
                    setFilterStation(stations.length === 1 ? stations[0].id : '');
                    setFilterDate('');
                    setFilterSearch('');
                    setPage(1);
                    fetchRecordings(1);
                  }}
                  size="small"
                  title="Clear filters"
                >
                  <Refresh />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Audio Player Bar */}
      {playingId && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'primary.dark' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => handlePlay(playingId)}
                sx={{ color: 'primary.main' }}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              <IconButton onClick={handleStop} size="small" sx={{ color: 'text.secondary' }}>
                <Stop />
              </IconButton>
              <Box sx={{ flex: 1, mx: 2 }}>
                <Slider
                  size="small"
                  value={currentTime}
                  max={duration || 100}
                  onChange={(_, val) => {
                    if (audioRef.current) audioRef.current.currentTime = val;
                    setCurrentTime(val);
                  }}
                  sx={{ py: 0 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right' }}>
                {formatDuration(Math.floor(currentTime))} / {formatDuration(Math.floor(duration))}
              </Typography>
              <VolumeUp sx={{ color: 'text.secondary', fontSize: 18 }} />
            </Box>
            {(() => {
              const rec = recordings.find(r => r.id === playingId);
              return rec ? (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 6 }}>
                  {rec.show_name} - {rec.station_name} - {formatTime(rec.start_time)}
                </Typography>
              ) : null;
            })()}
          </CardContent>
        </Card>
      )}

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => { setIsPlaying(false); }}
        onError={() => { setIsPlaying(false); }}
      />

      {/* Recordings Table */}
      <Card>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>Date / Time</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>Show</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>Station</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>Mount</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>Size</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }}>Storage</TableCell>
                <TableCell sx={{ color: 'primary.main', fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : recordings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No recordings found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recordings.map((rec) => (
                  <TableRow
                    key={rec.id}
                    sx={{
                      bgcolor: playingId === rec.id ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(rec.start_time).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(rec.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {rec.end_time && (
                          <> - {new Date(rec.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {rec.show_name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rec.station_name || `Station ${rec.station_id}`}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={rec.mount_label || rec.mountpoint}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2">{formatDuration(rec.duration_seconds)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatFileSize(rec.file_size)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getDropboxStatusIcon(rec.dropbox_status)}
                        label={rec.dropbox_status === 'uploaded' ? 'Dropbox' : rec.dropbox_status}
                        size="small"
                        color={getDropboxStatusColor(rec.dropbox_status)}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Play">
                        <IconButton
                          size="small"
                          onClick={() => handlePlay(rec.id)}
                          sx={{ color: playingId === rec.id && isPlaying ? 'primary.main' : 'text.secondary' }}
                        >
                          {playingId === rec.id && isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(rec.id)}
                          sx={{ color: 'text.secondary' }}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </Card>

      {/* Recording total */}
      {total > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
          {total} recording{total !== 1 ? 's' : ''} found
        </Typography>
      )}

      {/* Manage Recording Configs Dialog */}
      <Dialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RecordIcon sx={{ color: 'secondary.main' }} />
          Manage Recording Sources
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enable recording for the station's icecast mountpoints, or add any stream URL to record.
            Recordings are saved in hourly files and automatically uploaded to the Logger Dropbox account.
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Station Mountpoints
          </Typography>

          {availableMountpoints.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No icecast mountpoints configured on your stations. You can still record any stream
              by adding its URL below.
            </Alert>
          ) : (
            availableMountpoints.map((mp) => (
              <Box
                key={`${mp.station_id}-${mp.mountpoint}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: mp.config?.enabled ? 'secondary.dark' : 'divider',
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {mp.station_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {mp.mountpoint}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {mp.config?.enabled && (
                    <Chip
                      label="Recording"
                      size="small"
                      icon={<RecordIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{ bgcolor: 'rgba(239, 68, 68, 0.15)', color: 'secondary.light' }}
                    />
                  )}
                  <Switch
                    checked={mp.config?.enabled || false}
                    onChange={() => {
                      if (mp.configured) {
                        handleToggleConfig(mp.config);
                      } else {
                        handleAddConfig(mp.station_id, mp.mountpoint, mp.label);
                      }
                    }}
                    disabled={configLoading}
                    color="secondary"
                  />
                </Box>
              </Box>
            ))
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon sx={{ fontSize: 18 }} />
            Custom Stream URLs
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Record directly from any icecast mountpoint or internet stream by entering its full URL
            (e.g. http://localhost:8000/live or https://stream.example.com/mount).
          </Typography>

          {customConfigs.map((c) => {
            const station = stations.find(s => s.id === c.station_id);
            return (
              <Box
                key={c.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                  px: 2,
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: c.enabled ? 'secondary.dark' : 'divider',
                }}
              >
                <Box sx={{ minWidth: 0, mr: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                    {c.mount_label || c.stream_url}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {c.stream_url}{station ? ` — ${station.name}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  {c.enabled && (
                    <Chip
                      label="Recording"
                      size="small"
                      icon={<RecordIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{ bgcolor: 'rgba(239, 68, 68, 0.15)', color: 'secondary.light' }}
                    />
                  )}
                  <Switch
                    checked={c.enabled || false}
                    onChange={() => handleToggleConfig(c)}
                    disabled={configLoading}
                    color="secondary"
                  />
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteConfig(c)}
                      disabled={configLoading}
                      sx={{ color: 'text.secondary' }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              mt: customConfigs.length > 0 ? 1 : 0,
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            {stations.length > 1 && (
              <FormControl fullWidth size="small">
                <InputLabel>Station</InputLabel>
                <Select
                  value={customStation}
                  label="Station"
                  onChange={(e) => setCustomStation(e.target.value)}
                >
                  {stations.map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              size="small"
              label="Stream URL"
              placeholder="http://host:8000/mountpoint"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Label (optional)"
              placeholder="e.g. Main Output"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddCustomConfig}
              disabled={configLoading || !customUrl.trim() || !customStation}
            >
              Add & Start Recording
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* CSS for pulsing record icon */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </Box>
  );
}
